import { useContext } from 'react';
import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  useTheme
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import ListAltIcon from '@mui/icons-material/ListAlt';

import PortraitIcon from '@mui/icons-material/Portrait';
import HomeIcon from '@mui/icons-material/Home';

import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import useUser from 'src/contexts/UserContext';
import { useTranslation } from 'react-i18next';
const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[70]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }

    .MuiButtonBase-root {
      align-items: flex-start;

      & > .MuiButton-icon {
        margin-top: 4px;
      }
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[100]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          &[aria-disabled="true"] {
            color: ${theme.colors.alpha.trueWhite[50]};
          }

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
    'transform',
    'opacity'
  ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const userContext = useUser();
  const [t] = useTranslation();
  const theme = useTheme();

  return (
    <>

      <MenuWrapper>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sideMenu.dashboardsTitle')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/home"
                  startIcon={<HomeIcon />}
                >
                  {t('sideMenu.homeMenu')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sideMenu.managementTitle')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/workitems"
                  startIcon={<ListAltIcon />}
                >
                  {t('sideMenu.myTasksMenu')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disabled={userContext.getNavItemById(1000011) === undefined}
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/assignments"
                  startIcon={<ListAltIcon />}
                >
                  {t('sideMenu.assignments')}
                </Button>
              </ListItem>
              {process.env.REACT_APP_ENV_NAME == 'DEV' &&
                <>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/cras"
                      startIcon={<ListAltIcon />}
                    >
                      DEV: CRAs
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/workitemslist"
                      startIcon={<ListAltIcon />}
                    >
                      DEV: WorkItemsList
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disabled={userContext.getNavItemById(1000292) === undefined}
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/identities"
                      startIcon={<ListAltIcon />}
                    >
                      DEV: {t('sideMenu.identities')}
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/wsplayground"
                      startIcon={<ListAltIcon />}
                    >
                      DEV: WS Playground
                    </Button>
                  </ListItem>
                </>
              }
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/paymentsinitiation"
                  startIcon={<ListAltIcon />}
                >
                  {t('sideMenu.manualpayment.bankfiles')}
                </Button>
              </ListItem>
                <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/pendingbankfilerequests"
                  startIcon={<ListAltIcon />}
                >
                  {t('sideMenu.manualpayment.pendingBankfileRequests')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sideMenu.accountTitle')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/details"
                  startIcon={<PortraitIcon />}
                >
                  {t('sideMenu.userProfileMenu')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/signature"
                  startIcon={<PortraitIcon />}
                >
                  {t('sideMenu.userSignature')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        {process.env.REACT_APP_ENV_NAME == 'DEV' &&
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sideMenu.setup')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disabled={userContext.getNavItemById(1000008) === undefined}
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/setup/identities"
                    startIcon={<PortraitIcon />}
                  >
                    DEV: {t('sideMenu.identities')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/setup/surveys"
                    startIcon={<PortraitIcon />}
                  >
                    DEV: {t('sideMenu.surveys')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/setup/processplayground"
                    startIcon={<PortraitIcon />}
                  >
                    DEV: {t('sideMenu.processes')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/setup/graphqlplayground"
                    startIcon={<PortraitIcon />}
                  >
                    DEV: {t('sideMenu.graphqlplayground')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        }
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sideMenu.miscTitle')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/help"
                  startIcon={<LiveHelpIcon />}
                >
                  {t('sideMenu.helpMenu')}
                </Button>
              </ListItem>
              <ListItem>
                <Button
                  variant='contained'
                  fullWidth={true}
                  component={RouterLink}
                  onClick={closeSidebar}
                  startIcon={<PowerSettingsNewIcon />}
                  to="/logout"
                >
                  {t('sideMenu.logoutMenu')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
